import { useCallback, useEffect, useRef, useState } from "react";
import { NOVA_VOICE, pickNovaVoice } from "./character";

const MIC_STORAGE_KEY = "nova.microphoneDeviceId";

export type MicrophoneOption = {
  deviceId: string;
  label: string;
};

export type HeardUtterance = {
  text: string;
  alternatives: string[];
};

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  grammars?: unknown;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

const RecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  return (
    (window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionLike;
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    }).SpeechRecognition ||
    (window as Window & {
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    }).webkitSpeechRecognition ||
    null
  );
};

export const browserSpeechSupported = () => ({
  speak: typeof window !== "undefined" && "speechSynthesis" in window,
  listen: !!RecognitionCtor(),
  devices:
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.enumerateDevices,
});

const readStoredMicId = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(MIC_STORAGE_KEY) || "";
};

const loadSpeechVoices = () =>
  new Promise<SpeechSynthesisVoice[]>((resolve) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      resolve([]);
      return;
    }
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      resolve(existing);
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", finish, {
      once: true,
    });
    window.setTimeout(finish, 400);
  });

export const useBrowserSpeech = () => {
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [microphones, setMicrophones] = useState<MicrophoneOption[]>([]);
  const [selectedMicId, setSelectedMicIdState] = useState(readStoredMicId);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speakResolveRef = useRef<(() => void) | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const selectedMicIdRef = useRef(selectedMicId);
  selectedMicIdRef.current = selectedMicId;

  const stopSpeaking = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
    speakResolveRef.current?.();
    speakResolveRef.current = null;
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    setListening(false);
    setInterim("");
  }, []);

  const releaseMicrophone = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const listMicrophones = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    const mics = devices
      .filter((device) => device.kind === "audioinput")
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${index + 1}`,
      }));
    setMicrophones(mics);
    return mics;
  }, []);

  const openMicrophoneStream = useCallback(
    async (deviceId = selectedMicIdRef.current) => {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser cannot access a microphone.");
      }
      const audio: MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true }
        : { echoCancellation: true, noiseSuppression: true };
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio });
      } catch (error) {
        if (!deviceId) throw error;
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      }
      releaseMicrophone();
      streamRef.current = stream;
      const usedId = stream.getAudioTracks()[0]?.getSettings().deviceId || deviceId;
      if (usedId && usedId !== selectedMicIdRef.current) {
        selectedMicIdRef.current = usedId;
        setSelectedMicIdState(usedId);
        window.localStorage.setItem(MIC_STORAGE_KEY, usedId);
      }
      await listMicrophones();
      return stream;
    },
    [listMicrophones, releaseMicrophone],
  );

  const ensureMicrophone = useCallback(async () => {
    if (
      streamRef.current
        ?.getAudioTracks()
        .some((track) => track.readyState === "live")
    ) {
      await listMicrophones();
      return streamRef.current;
    }
    return openMicrophoneStream();
  }, [listMicrophones, openMicrophoneStream]);

  const setSelectedMicId = useCallback(
    async (deviceId: string) => {
      selectedMicIdRef.current = deviceId;
      setSelectedMicIdState(deviceId);
      if (deviceId) {
        window.localStorage.setItem(MIC_STORAGE_KEY, deviceId);
      }
      const wasListening = !!recognitionRef.current;
      stopListening();
      await openMicrophoneStream(deviceId);
      return wasListening;
    },
    [openMicrophoneStream, stopListening],
  );

  const stopAll = useCallback(() => {
    stopSpeaking();
    stopListening();
    releaseMicrophone();
  }, [releaseMicrophone, stopListening, stopSpeaking]);

  useEffect(() => () => stopAll(), [stopAll]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return undefined;
    const onChange = () => {
      void listMicrophones();
    };
    navigator.mediaDevices.addEventListener("devicechange", onChange);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", onChange);
    };
  }, [listMicrophones]);

  const speak = useCallback(
    async (text: string) => {
      if (typeof window === "undefined" || !window.speechSynthesis) {
        return;
      }
      stopSpeaking();
      const voices = await loadSpeechVoices();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = NOVA_VOICE.rate;
      utterance.pitch = NOVA_VOICE.pitch;
      const preferred = pickNovaVoice(voices);
      if (preferred) utterance.voice = preferred;
      await new Promise<void>((resolve) => {
        utterance.onend = () => {
          setSpeaking(false);
          speakResolveRef.current = null;
          resolve();
        };
        utterance.onerror = () => {
          setSpeaking(false);
          speakResolveRef.current = null;
          resolve();
        };
        speakResolveRef.current = resolve;
        setSpeaking(true);
        window.speechSynthesis.speak(utterance);
      });
    },
    [stopSpeaking],
  );

  const listen = useCallback((hints: string[] = []) => {
    return new Promise<HeardUtterance>((resolve, reject) => {
      const Ctor = RecognitionCtor();
      if (!Ctor) {
        reject(new Error("Voice input is not available in this browser."));
        return;
      }

      const startRecognition = () => {
        stopListening();
        const recognition = new Ctor();
        recognition.lang = "en-US";
        recognition.interimResults = true;
        recognition.maxAlternatives = 5;
        recognition.continuous = false;
        const extra = recognition as SpeechRecognitionLike & {
          deviceId?: string;
        };
        if (selectedMicIdRef.current) {
          extra.deviceId = selectedMicIdRef.current;
        }
        const grammarNames = hints
          .map((hint) => hint.replace(/[();|=<>]/g, " ").replace(/\s+/g, " ").trim())
          .filter((hint) => hint.length > 1)
          .slice(0, 40);
        const GrammarList =
          typeof window !== "undefined"
            ? (
                window as Window & {
                  SpeechGrammarList?: new () => {
                    addFromString: (grammar: string, weight?: number) => void;
                  };
                  webkitSpeechGrammarList?: new () => {
                    addFromString: (grammar: string, weight?: number) => void;
                  };
                }
              ).SpeechGrammarList ||
              (
                window as Window & {
                  webkitSpeechGrammarList?: new () => {
                    addFromString: (grammar: string, weight?: number) => void;
                  };
                }
              ).webkitSpeechGrammarList
            : null;
        if (GrammarList && grammarNames.length > 0) {
          try {
            const list = new GrammarList();
            list.addFromString(
              `#JSGF V1.0; grammar names; public <name> = ${grammarNames.join(" | ")};`,
              1,
            );
            recognition.grammars = list;
          } catch {
            // Grammar hints are optional; Chrome often ignores them.
          }
        }
        recognitionRef.current = recognition;
        setListening(true);
        setInterim("");

        recognition.onresult = (event: any) => {
          let finalText = "";
          let live = "";
          const alternatives: string[] = [];
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const result = event.results[i];
            const piece = result[0]?.transcript || "";
            if (result.isFinal) {
              finalText += piece;
              for (let alt = 0; alt < result.length; alt += 1) {
                const transcript = `${result[alt]?.transcript || ""}`.trim();
                if (transcript) alternatives.push(transcript);
              }
            } else {
              live += piece;
            }
          }
          setInterim(live);
          const text = finalText.trim();
          if (text) {
            setListening(false);
            setInterim("");
            resolve({
              text,
              alternatives: [...new Set(alternatives)].filter(
                (item) => item.toLowerCase() !== text.toLowerCase(),
              ),
            });
          }
        };
        recognition.onerror = (event: any) => {
          setListening(false);
          setInterim("");
          if (event?.error === "aborted" || event?.error === "no-speech") {
            resolve({ text: "", alternatives: [] });
            return;
          }
          reject(new Error(event?.error || "Could not hear that."));
        };
        recognition.onend = () => {
          setListening(false);
        };
        try {
          recognition.start();
        } catch (error) {
          setListening(false);
          reject(error);
        }
      };

      ensureMicrophone()
        .then(startRecognition)
        .catch(reject);
    });
  }, [ensureMicrophone, stopListening]);

  return {
    speaking,
    listening,
    interim,
    microphones,
    selectedMicId,
    setSelectedMicId,
    ensureMicrophone,
    speak,
    listen,
    stopAll,
    supported: browserSpeechSupported(),
  };
};
